import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, Link2, Pencil, Upload, Video } from "lucide-react";
import { uploadVideoChunked } from "@/lib/upload";
import LiveClassManager from "@/components/admin/LiveClassManager";
import YogaMembers from "@/components/admin/YogaMembers";

const isPlaceholder = (url) => (url || "").includes("res.cloudinary.com/demo");
const isLink = (url) => /youtube\.com|youtu\.be|vimeo\.com/i.test(url || "");

export const YogaTab = () => {
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/yoga/admin/lessons").then(({ data }) => setLessons(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setProgress(0);
    try {
      const { url } = await uploadVideoChunked(f, "yoga", { onProgress: setProgress });
      setEditing((l) => ({ ...l, video_url: url, video_type: "file" }));
      toast.success("Video uploaded — press Save to publish it");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProgress(null);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/yoga/admin/lessons/${editing.day}`, {
        title: editing.title,
        description: editing.description,
        level: editing.level,
        duration_minutes: parseInt(editing.duration_minutes, 10) || 0,
        video_url: editing.video_url,
        video_type: editing.video_type || (isLink(editing.video_url) ? "link" : "file"),
        cover_image: editing.cover_image,
      });
      toast.success(`Day ${editing.day} saved`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const pending = lessons.filter((l) => isPlaceholder(l.video_url)).length;

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div>
          <h3 className="font-display text-2xl">21-Day Yoga Videos</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Upload an MP4 (up to 100MB, sent in chunks) or paste a YouTube / Vimeo link for each day.
            Day 1 plays free for anyone with an account; Day 2 onwards needs a membership.
          </p>
        </div>
        <Badge
          variant="outline"
          className={pending ? "border-secondary/50 text-secondary bg-secondary/10" : "border-primary/40 text-primary bg-primary/10"}
          data-testid="yoga-pending-badge"
        >
          {pending ? `${pending} day(s) still on the demo video` : "All days have your own video"}
        </Badge>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {lessons.map((l) => (
          <Card key={l.day} className="border-border" data-testid={`yoga-day-${l.day}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="overline">Day {l.day}{l.day === 1 ? " · free preview" : ""}</div>
                  <div className="font-display text-xl leading-tight mt-1">{l.title}</div>
                </div>
                <button
                  onClick={() => setEditing({ ...l })}
                  className="text-primary hover:bg-accent p-1.5 rounded shrink-0"
                  data-testid={`yoga-edit-${l.day}`}
                  title="Edit day"
                >
                  <Pencil size={14}/>
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-3">
                <span className="flex items-center gap-1"><Clock size={12}/>{l.duration_minutes} min</span>
                <span className="flex items-center gap-1">
                  {isLink(l.video_url) ? <Link2 size={12}/> : <Video size={12}/>}
                  {isLink(l.video_url) ? "Link" : "File"}
                </span>
              </div>
              {isPlaceholder(l.video_url) && (
                <Badge variant="outline" className="text-[10px] mt-3 border-secondary/50 text-secondary" data-testid={`yoga-placeholder-${l.day}`}>
                  Demo video
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>


      <div className="mt-12 pt-10 border-t border-border">
        <LiveClassManager />
      </div>
      <YogaMembers />

      <Dialog open={Boolean(editing)} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Day {editing?.day} practice</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={editing.title || ""} onChange={(e)=>setEditing({...editing,title:e.target.value})} data-testid="yoga-title"/></div>
              <div><Label>Description</Label><Textarea rows={3} value={editing.description || ""} onChange={(e)=>setEditing({...editing,description:e.target.value})} data-testid="yoga-desc"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Level</Label><Input value={editing.level || ""} onChange={(e)=>setEditing({...editing,level:e.target.value})} data-testid="yoga-level"/></div>
                <div><Label>Duration (minutes)</Label><Input type="number" value={editing.duration_minutes || 0} onChange={(e)=>setEditing({...editing,duration_minutes:e.target.value})} data-testid="yoga-duration"/></div>
              </div>

              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                <Label className="overline">Video for this day</Label>
                <div>
                  <div className="text-xs text-muted-foreground mb-1.5">Paste a YouTube / Vimeo link</div>
                  <Input
                    value={editing.video_url || ""}
                    onChange={(e)=>setEditing({...editing, video_url:e.target.value, video_type: isLink(e.target.value) ? "link" : "file"})}
                    placeholder="https://youtu.be/…"
                    data-testid="yoga-video-url"
                  />
                </div>
                <div className="text-xs text-muted-foreground">— or upload an MP4 / MOV / WebM (max 100MB) —</div>
                <Input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={onVideo} data-testid="yoga-video-file"/>
                {progress !== null && (
                  <div data-testid="yoga-upload-progress">
                    <div className="h-2 rounded-full bg-border overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }}/>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                      <Upload size={12}/> Uploading… {progress}%
                    </div>
                  </div>
                )}
                {editing.video_url && !isLink(editing.video_url) && (
                  <video src={mediaUrl(editing.video_url)} controls className="w-full rounded-lg aspect-video bg-black" data-testid="yoga-video-preview"/>
                )}
              </div>

              <Button onClick={save} disabled={busy || progress !== null} className="bg-primary text-primary-foreground w-full" data-testid="yoga-save">
                {busy ? "Saving…" : `Save Day ${editing.day}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default YogaTab;
